// src/Admin/AcademyAdmin.js
// Academy CMS — create & manage study tracks, courses, lessons, videos,
// practice labs and quizzes. Persists to Realtime Database (see academyDb.js).
import React, { useEffect, useRef, useState } from 'react';
import {
  subscribeAcademy,
  writeTopic,
  writeCourse,
  writeLesson,
  removeTopic,
  removeCourse,
  removeLesson,
  reorderLessons,
  uploadImage,
  uploadFile,
  uploadVideo,
  generateLab,
  seedAcademy,
  slugify,
  uniqueId,
} from './academyDb';
import './AcademyAdmin.css';

/* ── Tiny inline icons ───────────────────────────────────────── */
const Chevron = ({ dir = 'right' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    style={{ transform: dir === 'right' ? 'none' : dir === 'left' ? 'rotate(180deg)' : dir === 'down' ? 'rotate(90deg)' : 'rotate(-90deg)' }}>
    <path d="M9 18l6-6-6-6" />
  </svg>
);
const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
  </svg>
);
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const IconTrack = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);
const IconCourse = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2 2 7l10 5 10-5-10-5z" /><path d="m2 17 10 5 10-5" /><path d="m2 12 10 5 10-5" />
  </svg>
);
const IconLesson = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="3" width="20" height="14" rx="2" /><path d="m10 8 4 2.5L10 13V8z" fill="currentColor" stroke="none" /><path d="M8 21h8" />
  </svg>
);
const IconQuiz = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" /><path d="M12 17h.01" />
  </svg>
);
const IconLab = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14.5 2v6.5l4.6 8.2A2 2 0 0 1 17.4 20H6.6a2 2 0 0 1-1.7-3.3L9.5 8.5V2" /><path d="M9 2h6" /><path d="M7.5 13h9" />
  </svg>
);
const IconVideo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m22 8-6 4 6 4V8z" /><rect x="2" y="6" width="14" height="12" rx="2" />
  </svg>
);
const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
  </svg>
);
const IconFile = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" />
  </svg>
);
const IconTrophy = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);
const IconImage = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" />
  </svg>
);
const fileGlyph = (type) => {
  if (type === 'image') return <IconImage />;
  return <IconFile />;
};
const baseName = (name) => (name || '').replace(/\.[^.]+$/, '');

/* ── Form primitives ─────────────────────────────────────────── */
const Field = ({ label, hint, children }) => (
  <div className="aca-field">
    <span className="aca-field-label">{label}</span>
    {hint && <span className="aca-field-hint">{hint}</span>}
    {children}
  </div>
);

const Text = ({ value, onChange, placeholder }) => (
  <input className="aca-input" value={value ?? ''} placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)} />
);
const Area = ({ value, onChange, placeholder, rows = 3 }) => (
  <textarea className="aca-textarea" rows={rows} value={value ?? ''} placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)} />
);
const Num = ({ value, onChange, placeholder, step }) => (
  <input className="aca-input" type="number" step={step} value={value ?? ''} placeholder={placeholder}
    onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
);
const Select = ({ value, onChange, options }) => (
  <select className="aca-input" value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const Section = ({ title, desc, icon, count, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`aca-section ${open ? 'is-open' : ''}`}>
      <button type="button" className="aca-section-head" onClick={() => setOpen((v) => !v)}>
        {icon && <span className="aca-section-icon">{icon}</span>}
        <span className="aca-section-headtext">
          <span className="aca-section-title">{title}</span>
          {desc && <span className="aca-section-desc">{desc}</span>}
        </span>
        {count != null && <span className="aca-section-count">{count}</span>}
        <span className="aca-section-chevron"><Chevron dir={open ? 'down' : 'right'} /></span>
      </button>
      {open && <div className="aca-section-body">{children}</div>}
    </div>
  );
};

/* Structured content block (header on top, controls below). */
const BlockCard = ({ title, desc, icon, children }) => (
  <section className="aca-block">
    <div className="aca-block-head">
      {icon && <span className="aca-block-icon">{icon}</span>}
      <div className="aca-block-headtext">
        <h4 className="aca-block-title">{title}</h4>
        {desc && <p className="aca-block-desc">{desc}</p>}
      </div>
    </div>
    <div className="aca-block-body">{children}</div>
  </section>
);

/* Debounced auto-save. Calls saveFn(draft) ~700ms after the last change.
   Skips the initial mount so opening an item doesn't trigger a write. */
const useAutoSave = (draft, saveFn) => {
  const [status, setStatus] = useState('saved'); // 'saved' | 'saving'
  const first = useRef(true);
  const saveRef = useRef(saveFn);
  saveRef.current = saveFn;

  useEffect(() => {
    if (first.current) { first.current = false; return undefined; }
    setStatus('saving');
    const t = setTimeout(async () => {
      try { await saveRef.current(draft); } finally { setStatus('saved'); }
    }, 700);
    return () => clearTimeout(t);
  }, [draft]);

  return status;
};

/* Subtle auto-save status pill. */
const SaveStatus = ({ status }) => (
  <span className={`aca-savestatus is-${status}`}>
    <span className="aca-savestatus-dot" />
    {status === 'saving' ? 'Speichert…' : 'Gespeichert'}
  </span>
);

/* Generic editor for an array of objects. renderItem(item, set, index). */
const ListEditor = ({ items, onChange, blank, addLabel, renderItem, empty, itemLabel }) => {
  const list = Array.isArray(items) ? items : [];
  const setAt = (i, v) => onChange(list.map((it, idx) => (idx === i ? v : it)));
  const add = () => onChange([...list, typeof blank === 'function' ? blank() : { ...blank }]);
  const removeAt = (i) => onChange(list.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="aca-list">
      {list.length === 0 && empty && <p className="aca-list-empty">{empty}</p>}
      {list.map((item, i) => (
        <div className="aca-list-item" key={i}>
          <div className="aca-list-item-head">
            <span className="aca-list-no">{itemLabel ? `${itemLabel} ${i + 1}` : i + 1}</span>
            <div className="aca-list-item-actions">
              <button type="button" className="aca-icon-btn" onClick={() => move(i, -1)} title="Nach oben" disabled={i === 0}>↑</button>
              <button type="button" className="aca-icon-btn" onClick={() => move(i, 1)} title="Nach unten" disabled={i === list.length - 1}>↓</button>
              <button type="button" className="aca-icon-btn is-danger" onClick={() => removeAt(i)} title="Entfernen"><TrashIcon /></button>
            </div>
          </div>
          <div className="aca-list-item-body">{renderItem(item, (v) => setAt(i, v), i)}</div>
        </div>
      ))}
      <button type="button" className="aca-add-row" onClick={add}>
        <PlusIcon /> {addLabel}
      </button>
    </div>
  );
};

/* Editor for an array of plain strings. */
const StringList = ({ items, onChange, addLabel, placeholder }) => (
  <ListEditor
    items={items}
    onChange={onChange}
    blank={() => ''}
    addLabel={addLabel}
    renderItem={(item, set) => <Text value={item} onChange={set} placeholder={placeholder} />}
  />
);

/* Image upload to Firebase Storage with live preview. */
const ImageUploader = ({ value, onChange, scope }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const handle = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const { url } = await uploadImage(file, scope);
      onChange(url);
    } catch (ex) {
      setErr(ex.message || 'Upload fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };
  if (value) {
    return (
      <div className="aca-uploader">
        <div className="aca-img-frame">
          <img src={value} alt="Vorschau" className="aca-img-preview" />
          <div className="aca-img-overlay">
            <label className="aca-btn aca-btn-ghost aca-btn-sm">
              {busy ? 'Lädt…' : 'Ersetzen'}
              <input type="file" accept="image/*" hidden onChange={handle} disabled={busy} />
            </label>
            <button type="button" className="aca-btn aca-btn-danger aca-btn-sm" onClick={() => onChange('')}>Entfernen</button>
          </div>
        </div>
        {err && <span className="aca-err">{err}</span>}
      </div>
    );
  }

  return (
    <div className="aca-uploader">
      <label className={`aca-file-drop ${busy ? 'is-busy' : ''}`}>
        <span className="aca-file-drop-icon"><UploadIcon /></span>
        <span className="aca-file-drop-text">{busy ? 'Lädt hoch…' : 'Bild hochladen'}</span>
        <span className="aca-file-drop-hint">PNG, JPG, WebP · max. 10 MB</span>
        <input type="file" accept="image/*" hidden onChange={handle} disabled={busy} />
      </label>
      {err && <span className="aca-err">{err}</span>}
    </div>
  );
};

/* Video upload → temporary Firebase Storage → Bunny CDN migration.
   Writes the resulting public CDN URL back via onChange(url). */
/* Format seconds → display label (m:ss or h:mm:ss). */
const formatDuration = (s) => {
  const total = Math.round(Number(s) || 0);
  if (!total) return '';
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
};

/* Read a video file's length client-side via a temporary <video> element. */
const readVideoMeta = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const v = document.createElement('video');
  v.preload = 'metadata';
  v.onloadedmetadata = () => {
    URL.revokeObjectURL(url);
    const seconds = Math.round(v.duration);
    if (!seconds || !Number.isFinite(seconds)) { reject(new Error('no duration')); return; }
    resolve({ seconds, label: formatDuration(seconds) });
  };
  v.onerror = () => { URL.revokeObjectURL(url); reject(new Error('metadata failed')); };
  v.src = url;
});

const VideoUploader = ({ value, onChange, onPathChange, onMeta }) => {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState('');

  const handle = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setBusy(true); setErr(''); setProgress(0);
    let meta = null;
    try { meta = await readVideoMeta(file); } catch (_) { /* duration optional */ }
    try {
      const { videoUrl, path: storagePath } = await uploadVideo(file, {
        onProgress: setProgress,
      });
      onChange(videoUrl);
      if (onPathChange) onPathChange(storagePath);
      if (meta && onMeta) onMeta(meta);
    } catch (ex) {
      setErr(ex.message || 'Upload fehlgeschlagen');
    } finally {
      setBusy(false); setProgress(0);
      if (e.target) e.target.value = '';
    }
  };

  const busyLabel = `Lädt hoch… ${progress}%`;

  if (value && !busy) {
    return (
      <div className="aca-uploader">
        <div className="aca-img-frame">
          <video src={value} controls preload="metadata" className="aca-uploader-video" />
          <div className="aca-img-overlay">
            <label className="aca-btn aca-btn-ghost aca-btn-sm">
              Ersetzen
              <input type="file" accept="video/*" hidden onChange={handle} disabled={busy} />
            </label>
            <button type="button" className="aca-btn aca-btn-danger aca-btn-sm" onClick={() => onChange('')}>Entfernen</button>
          </div>
        </div>
        {err && <span className="aca-err">{err}</span>}
      </div>
    );
  }

  return (
    <div className="aca-uploader">
      <label className={`aca-file-drop ${busy ? 'is-busy' : ''}`}>
        <span className="aca-file-drop-icon"><UploadIcon /></span>
        <span className="aca-file-drop-text">{busy ? busyLabel : 'Video hochladen'}</span>
        <span className="aca-file-drop-hint">MP4, MOV, WebM · wird in Firebase Storage gespeichert</span>
        <input type="file" accept="video/*" hidden onChange={handle} disabled={busy} />
      </label>
      {busy && (
        <div className="aca-vid-progress">
          <div className="aca-vid-progress-bar" style={{ width: `${progress}%` }} />
        </div>
      )}
      {err && <span className="aca-err">{err}</span>}
    </div>
  );
};

/* File upload (PDF / Doc / image) to Firebase Storage for resources &
   cheatsheets. Shows a drop-style button, then a file chip once uploaded.
   onChange receives the upload meta { url, name, type, path, size } or null. */
const FileUploader = ({ value, onChange, scope = 'files', accept }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const has = !!(value && value.url);

  const handle = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const meta = await uploadFile(file, scope);
      onChange(meta);
    } catch (ex) {
      setErr(ex.message || 'Upload fehlgeschlagen');
    } finally {
      setBusy(false);
      if (e.target) e.target.value = '';
    }
  };

  if (has) {
    return (
      <div className="aca-file">
        <div className="aca-file-chip">
          <span className="aca-file-chip-icon">{fileGlyph(value.type)}</span>
          <span className="aca-file-chip-name">{value.name || 'Datei'}</span>
          <a className="aca-file-chip-link" href={value.url} target="_blank" rel="noreferrer" title="Ansehen">Ansehen</a>
          <label className="aca-file-chip-replace" title="Ersetzen">
            {busy ? '…' : 'Ersetzen'}
            <input type="file" accept={accept} hidden onChange={handle} disabled={busy} />
          </label>
          <button type="button" className="aca-icon-btn is-danger" onClick={() => onChange(null)} title="Entfernen"><TrashIcon /></button>
        </div>
        {err && <span className="aca-err">{err}</span>}
      </div>
    );
  }

  return (
    <div className="aca-file">
      <label className={`aca-file-drop ${busy ? 'is-busy' : ''}`}>
        <span className="aca-file-drop-icon"><UploadIcon /></span>
        <span className="aca-file-drop-text">{busy ? 'Lädt hoch…' : 'Datei hochladen'}</span>
        <span className="aca-file-drop-hint">PDF, Word, Bild · max. 25 MB</span>
        <input type="file" accept={accept} hidden onChange={handle} disabled={busy} />
      </label>
      {err && <span className="aca-err">{err}</span>}
    </div>
  );
};

/* Lesson types shown in the creation picker. Each lesson is ONE type. */
const LESSON_TYPES = [
  { id: 'video', label: 'Video-Lektion', desc: 'Video (Upload oder URL) inkl. Cheatsheets / Downloads.', accent: '#f078a8', icon: <IconVideo />, build: () => ({ type: 'video', video: { provider: 'bunny' }, cheatsheets: [] }) },
  { id: 'practice', label: 'Practice Lab', desc: 'Interaktive Schreibübung (E-Mail, Betreff, Template).', accent: '#5fcdd0', icon: <IconLab />, build: () => ({ type: 'practice', practiceLab: { mode: 'easy', title: '', brief: '' } }) },
  { id: 'quiz', label: 'Mini-Quiz', desc: 'Wissensabfrage mit mehreren Fragen.', accent: '#f0b85f', icon: <IconQuiz />, build: () => ({ type: 'quiz', quiz: { questions: [] } }) },
];

/* Resolve a lesson's type (explicit field, with inference for legacy data). */
const resolveLessonType = (lesson) => {
  if (lesson?.type === 'video' || lesson?.type === 'practice' || lesson?.type === 'quiz') return lesson.type;
  if (lesson?.practiceLab) return 'practice';
  if (lesson?.quiz) return 'quiz';
  return 'video';
};

/* Lesson creation modal: pick a building block – name & details follow in the editor. */
const LessonCreateModal = ({ onClose, onCreate }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pick = (type) => { onCreate(type); onClose(); };

  return (
    <div className="aca-modal-overlay" onClick={onClose} role="presentation">
      <div className="aca-modal aca-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="aca-modal-head">
          <span className="aca-modal-eyebrow">Neue Lektion</span>
          <h3 className="aca-modal-title">Womit soll die Lektion starten?</h3>
          <p className="aca-modal-text">Wähle einen Baustein – Name und alle weiteren Details legst du direkt danach im Editor fest. Andere Bausteine kannst du jederzeit ergänzen.</p>
        </div>

        <div className="aca-ltype-grid">
          {LESSON_TYPES.map((t) => (
            <button
              type="button"
              key={t.id}
              className="aca-ltype"
              style={{ '--ltype-accent': t.accent }}
              onClick={() => pick(t.id)}
            >
              <span className="aca-ltype-icon">{t.icon}</span>
              <span className="aca-ltype-text">
                <span className="aca-ltype-label">{t.label}</span>
                <span className="aca-ltype-desc">{t.desc}</span>
              </span>
              <span className="aca-ltype-arrow"><Chevron /></span>
            </button>
          ))}
        </div>

        <div className="aca-modal-actions">
          <button type="button" className="aca-btn aca-btn-ghost" onClick={onClose}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
};

/* ── Practice Lab editor ─────────────────────────────────────── */
// The mode defines the whole exercise — it's picked first, as tabs, with a
// short explanation of what each one does and who it's for.
const MODE_INFO = [
  {
    value: 'easy',
    label: 'Einfacher Modus',
    tag: 'Geführt',
    desc: 'Feste E-Mail-Struktur: Der Nutzer füllt vorgegebene Abschnitte (Icebreaker, Pitch, CTA …) Schritt für Schritt aus. Ideal für Einsteiger.',
  },
  {
    value: 'hard',
    label: 'Schwerer Modus',
    tag: 'Frei',
    desc: 'Nur ein Briefing: Der Nutzer schreibt Betreff + komplette E-Mail frei, orientiert an einer psychologischen Struktur. Für Fortgeschrittene.',
  },
  {
    value: 'subject',
    label: 'Betreffzeilen',
    tag: 'Fokus',
    desc: 'Der Nutzer schreibt mehrere Betreffzeilen und übt Hooks – unterstützt durch Angle-Tipps. Trainiert gezielt den Einstieg.',
  },
  {
    value: 'salesgod',
    label: 'SalesGod',
    tag: 'Templates',
    desc: 'Dynamische Templates mit {{greeting}}, {{variablen}} und { KI-Prompts }. Lehrt das Schreiben skalierbarer Vorlagen statt Einzel-Mails.',
  },
];

const ModePicker = ({ value, onChange }) => {
  const active = value || 'easy';
  return (
    <div className="aca-modetabs">
      {MODE_INFO.map((m) => (
        <button
          key={m.value}
          type="button"
          className={`aca-modetab ${active === m.value ? 'is-active' : ''}`}
          onClick={() => onChange(m.value)}
        >
          <span className="aca-modetab-head">
            <span className="aca-modetab-label">{m.label}</span>
            <span className="aca-modetab-tag">{m.tag}</span>
          </span>
          <span className="aca-modetab-desc">{m.desc}</span>
        </button>
      ))}
    </div>
  );
};

/* Example JSON schema per mode – copy this, hand it to an AI, paste the result back. */
const labExample = (mode = 'easy') => {
  const context = {
    scenario: 'Kurze Beschreibung der Ausgangslage für den User.',
    facts: [{ label: 'Branche', value: 'SaaS' }, { label: 'Rolle', value: 'Head of Sales' }],
    notes: ['Nicht zu werblich klingen', 'Konkret und kurz bleiben'],
  };
  if (mode === 'hard') {
    return {
      mode: 'hard', title: 'Freie Cold E-Mail', brief: 'Schreibe eine überzeugende Cold E-Mail.',
      charLimit: 700, wordLimit: 120,
      structure: [
        { label: 'Hook', hint: 'Personalisierter Einstieg', words: 20 },
        { label: 'Problem', hint: 'Relevanter Schmerzpunkt', words: 30 },
        { label: 'Lösung', hint: 'Dein Mehrwert', words: 35 },
        { label: 'CTA', hint: 'Klare, einfache Frage', words: 15 },
      ],
      context,
    };
  }
  if (mode === 'subject') {
    return {
      mode: 'subject', title: 'Betreffzeilen schreiben', brief: 'Schreibe starke Betreffzeilen.',
      charLimit: 60,
      angles: [
        { name: 'Neugier', hint: 'Offene Schleife erzeugen', example: 'Eine Frage zu {{business_name}}' },
        { name: 'Nutzen', hint: 'Konkreten Vorteil nennen', example: '20 % mehr Antworten?' },
      ],
      context,
    };
  }
  if (mode === 'salesgod') {
    return {
      mode: 'salesgod', title: 'Dynamisches Template', brief: 'Schreibe ein E-Mail-Template mit Variablen, Snippets und KI-Prompts.',
      variables: [
        { token: 'greeting', label: 'Anrede-Snippet', sample: 'Hi Sarah,', special: true },
        { token: 'business_name', label: 'Firmenname', sample: 'Acme GmbH', special: false },
      ],
      promptExamples: ['ein Bezug zu einem aktuellen Projekt', 'ein passendes Kompliment'],
      starter: '{{greeting}}\n\nmir ist { ein Bezug zu {{business_name}} } aufgefallen …',
      context,
    };
  }
  return {
    mode: 'easy', title: 'Cold E-Mail aufbauen', brief: 'Schreibe eine kurze, personalisierte Cold E-Mail.',
    email: {
      toName: 'Sarah Klein', to: 'sarah@beispiel.de',
      subjectPlaceholder: 'Kurze Frage zu …', greeting: 'Hi Sarah,',
      signoff: 'Beste Grüße,\nMax', note: 'Halte es kurz und konkret.',
    },
    blocks: [
      { id: 'icebreaker', label: 'Eisbrecher', hint: 'Persönlicher Bezug', placeholder: 'Mir ist aufgefallen …', wordLimit: 20 },
      { id: 'value', label: 'Mehrwert', hint: 'Was hat der Empfänger davon?', placeholder: 'Wir helfen …', wordLimit: 30 },
      { id: 'cta', label: 'Call to Action', hint: 'Klare, einfache Frage', placeholder: 'Hast du Donnerstag 10 Min.?', wordLimit: 15 },
    ],
    context,
  };
};

/* Example JSON schema for a Mini-Quiz – covers all three question types. */
const quizExample = () => ({
  questions: [
    {
      id: 'q1', type: 'multiple-choice',
      question: 'Was gehört in den ersten Satz einer Cold E-Mail?',
      options: ['Ein persönlicher Bezug', 'Die Preisliste', 'Eine lange Firmenvorstellung'],
      correctIndex: 0,
    },
    {
      id: 'q2', type: 'text',
      question: 'Formuliere einen Eisbrecher für einen SaaS-Gründer.',
      placeholder: 'Mir ist aufgefallen, dass …',
    },
    {
      id: 'q3', type: 'email',
      question: 'Schreibe eine kurze Cold E-Mail.',
      subjectPlaceholder: 'Kurze Frage zu …',
      bodyPlaceholder: 'Hi …, mir ist aufgefallen …',
    },
  ],
});

/* Paste / upload JSON to quickly fill an editor section (e.g. AI-generated). */
const JsonImport = ({ getExample, onImport, hint, requireArrayKey }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setError(''); setOk(false); };

  const doImport = () => {
    reset();
    let parsed;
    try { parsed = JSON.parse(text); }
    catch (e) { setError(`Ungültiges JSON: ${e.message}`); return; }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setError('Das JSON muss ein Objekt sein.');
      return;
    }
    if (requireArrayKey && !Array.isArray(parsed[requireArrayKey])) {
      setError(`Das JSON braucht ein Array "${requireArrayKey}".`);
      return;
    }
    onImport(parsed);
    setOk(true);
    setText('');
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    reset();
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ''));
    reader.onerror = () => setError('Datei konnte nicht gelesen werden.');
    reader.readAsText(f);
    e.target.value = '';
  };

  const schema = () => JSON.stringify(getExample(), null, 2);
  const copySchema = () => { try { navigator.clipboard?.writeText(schema()); } catch (_) { /* noop */ } };
  const insertSchema = () => { reset(); setText(schema()); };

  return (
    <div className="aca-jsonimport">
      <button type="button" className={`aca-json-toggle ${open ? 'is-open' : ''}`} onClick={() => setOpen((o) => !o)}>
        <IconFile /> Per JSON importieren
        <span className="aca-json-chev"><Chevron /></span>
      </button>
      {open && (
        <div className="aca-json-body">
          <p className="aca-json-desc">
            {hint || 'Schema kopieren → einer KI geben → ausgefülltes JSON hier einfügen oder als .json hochladen → importieren.'}
          </p>
          <div className="aca-json-actions">
            <button type="button" className="aca-btn aca-btn-ghost aca-btn-sm" onClick={copySchema}>Schema kopieren</button>
            <button type="button" className="aca-btn aca-btn-ghost aca-btn-sm" onClick={insertSchema}>Schema einfügen</button>
            <button type="button" className="aca-btn aca-btn-ghost aca-btn-sm" onClick={() => fileRef.current?.click()}>.json hochladen</button>
            <input ref={fileRef} type="file" accept="application/json,.json" hidden onChange={onFile} />
          </div>
          <textarea
            className="aca-textarea aca-json-text"
            value={text}
            onChange={(e) => { setText(e.target.value); reset(); }}
            rows={9}
            spellCheck={false}
            placeholder={'{\n  "…": "…"\n}'}
          />
          {error && <p className="aca-json-err">{error}</p>}
          {ok && <p className="aca-json-ok">Importiert ✓</p>}
          <button type="button" className="aca-btn aca-btn-primary aca-btn-sm" onClick={doImport} disabled={!text.trim()}>
            Importieren
          </button>
        </div>
      )}
    </div>
  );
};

const IconSpark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8" />
  </svg>
);

/* Difficulty levels for AI generation. */
const LAB_LEVELS = [
  { value: 'anfaenger', label: 'Anfänger', desc: 'Kurz, viel Hilfe' },
  { value: 'mittel', label: 'Mittel', desc: 'Realistisch' },
  { value: 'profi', label: 'Profi', desc: 'Anspruchsvoll' },
];

/* Click-to-add starter blocks per mode, so you don't start from scratch. */
const BLOCK_PRESETS = [
  { id: 'icebreaker', label: 'Eisbrecher', hint: 'Persönlicher, konkreter Bezug zum Empfänger', placeholder: 'Mir ist aufgefallen, dass …', wordLimit: 20 },
  { id: 'observation', label: 'Beobachtung', hint: 'Etwas Spezifisches über die Firma/Branche', placeholder: 'Bei euch im Bereich … sehe ich oft …', wordLimit: 25 },
  { id: 'problem', label: 'Problem', hint: 'Der relevante Schmerzpunkt der Zielgruppe', placeholder: 'Viele … kämpfen damit, dass …', wordLimit: 25 },
  { id: 'value', label: 'Mehrwert', hint: 'Was hat der Empfänger konkret davon?', placeholder: 'Wir helfen …, indem …', wordLimit: 30 },
  { id: 'proof', label: 'Social Proof', hint: 'Glaubwürdiger Beleg, kein erfundenes Detail', placeholder: 'Für … konnten wir …', wordLimit: 25 },
  { id: 'cta', label: 'Call to Action', hint: 'Eine klare, leicht zu beantwortende Frage', placeholder: 'Hast du Donnerstag 10 Minuten?', wordLimit: 15 },
  { id: 'ps', label: 'P.S.', hint: 'Optionaler Nachsatz, der neugierig macht', placeholder: 'P.S. …', wordLimit: 15 },
];
const STRUCTURE_PRESETS = [
  { label: 'Hook', hint: 'Personalisierter Einstieg', words: 20 },
  { label: 'Problem', hint: 'Relevanter Schmerzpunkt', words: 30 },
  { label: 'Lösung', hint: 'Dein konkreter Mehrwert', words: 30 },
  { label: 'Beleg', hint: 'Glaubwürdiger Grund/Proof', words: 20 },
  { label: 'CTA', hint: 'Klare, einfache Frage', words: 15 },
];
const ANGLE_PRESETS = [
  { name: 'Neugier', hint: 'Offene Schleife erzeugen', example: 'Eine kurze Frage zu eurem Onboarding' },
  { name: 'Nutzen', hint: 'Konkreten Vorteil nennen', example: 'Schneller von Lead zu Termin?' },
  { name: 'Frage', hint: 'Ehrliche, kurze Frage stellen', example: 'Wie löst ihr das gerade?' },
  { name: 'Beobachtung', hint: 'Spezifisches Branchen-Detail', example: 'Idee für eure Sommer-Saison' },
  { name: 'Personalisierung', hint: 'Direkter Bezug zur Firma', example: 'Gesehen bei [Firma]: …' },
];
const VARIABLE_PRESETS = [
  { token: 'greeting', label: 'Anrede-Snippet', sample: 'Hi Sarah,', special: true },
  { token: 'first_name', label: 'Vorname', sample: 'Sarah', special: false },
  { token: 'business_name', label: 'Firmenname', sample: 'Acme GmbH', special: false },
  { token: 'industry', label: 'Branche', sample: 'SaaS', special: false },
  { token: 'city', label: 'Stadt', sample: 'Berlin', special: false },
];

/* A row of click-to-add preset chips above a ListEditor. */
const PresetRow = ({ presets, onAdd, getKey, isUsed, label = 'Schnell hinzufügen' }) => (
  <div className="aca-presets">
    <span className="aca-presets-label">{label}</span>
    <div className="aca-presets-chips">
      {presets.map((p) => {
        const used = isUsed ? isUsed(p) : false;
        return (
          <button
            key={getKey(p)}
            type="button"
            className={`aca-preset-chip ${used ? 'is-used' : ''}`}
            onClick={() => onAdd(p)}
            title={p.hint || ''}
          >
            <PlusIcon /> {p.label || p.name || p.token}
          </button>
        );
      })}
    </div>
  </div>
);

/* AI helper: generate a full lab draft for the selected mode + difficulty. */
const AiLabGenerator = ({ mode, onResult, onLessonMeta }) => {
  const [difficulty, setDifficulty] = useState('mittel');
  const [topic, setTopic] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const run = async () => {
    if (busy) return;
    setBusy(true); setError(''); setDone(false);
    try {
      const { lab, lesson } = await generateLab({ mode: mode || 'easy', difficulty, topic: topic.trim() });
      onResult(lab);
      if (lesson && onLessonMeta) onLessonMeta(lesson);
      setDone(true);
    } catch (e) {
      setError(e.message || 'Generierung fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="aca-ai">
      <div className="aca-ai-head">
        <span className="aca-ai-icon"><IconSpark /></span>
        <div className="aca-ai-headtext">
          <span className="aca-ai-title">Mit KI erstellen</span>
          <span className="aca-ai-sub">Lass eine komplette Übung vorschlagen – danach einfach anpassen oder neu generieren.</span>
        </div>
      </div>

      <div className="aca-ai-levels">
        {LAB_LEVELS.map((l) => (
          <button
            key={l.value}
            type="button"
            className={`aca-ai-level ${difficulty === l.value ? 'is-active' : ''}`}
            onClick={() => setDifficulty(l.value)}
          >
            <span className="aca-ai-level-label">{l.label}</span>
            <span className="aca-ai-level-desc">{l.desc}</span>
          </button>
        ))}
      </div>

      <textarea
        className="aca-textarea aca-ai-topic"
        rows={2}
        value={topic}
        onChange={(e) => setTopic(e.target.value)}
        placeholder="Worum soll es gehen? z. B. Zielgruppe, Branche, Angebot, Tonalität (optional)"
      />

      <div className="aca-ai-foot">
        <button type="button" className="aca-btn aca-btn-primary aca-btn-sm" onClick={run} disabled={busy}>
          <IconSpark /> {busy ? 'Generiert…' : done ? 'Neu generieren' : 'Übung generieren'}
        </button>
        {done && !busy && <span className="aca-ai-ok">Übernommen ✓ Du kannst alles unten anpassen.</span>}
        {error && <span className="aca-ai-err">{error}</span>}
      </div>
    </div>
  );
};

const PracticeLabEditor = ({ value, onChange, onLessonMeta }) => {
  const enabled = !!value;
  const lab = value || {};
  const set = (k, v) => onChange({ ...lab, [k]: v });
  const setContext = (k, v) => onChange({ ...lab, context: { ...(lab.context || {}), [k]: v } });
  const setEmail = (k, v) => onChange({ ...lab, email: { ...(lab.email || {}), [k]: v } });

  if (!enabled) {
    return (
      <div className="aca-lab-empty">
        <button type="button" className="aca-add-row" onClick={() => onChange({ mode: 'easy', title: '', brief: '' })}>
          <PlusIcon /> Practice-Lab-Aufgabe hinzufügen
        </button>
        <JsonImport
          getExample={() => labExample('easy')}
          hint="Schema kopieren → einer KI geben → ausgefülltes JSON einfügen oder als .json hochladen → importieren. Erstellt direkt die Aufgabe."
          onImport={(data) => onChange({ mode: 'easy', ...data })}
        />
      </div>
    );
  }

  const ctx = lab.context || {};
  return (
    <div className="aca-sub">
      {/* Mode first — it's the most important decision and shapes everything below. */}
      <div className="aca-modeblock">
        <div className="aca-modeblock-head">
          <span className="aca-modeblock-title">Übungs-Modus</span>
          <span className="aca-modeblock-sub">Wähle zuerst die Art der Übung. Jeder Modus funktioniert anders.</span>
        </div>
        <ModePicker value={lab.mode} onChange={(v) => set('mode', v)} />
      </div>

      {/* AI: the fastest way to start — generate a full draft, then tweak. */}
      <AiLabGenerator
        mode={lab.mode}
        onResult={(generated) => onChange({ ...lab, ...generated })}
        onLessonMeta={onLessonMeta}
      />

      <Field label="Titel" hint="Wird dem Nutzer als Überschrift der Übung angezeigt.">
        <Text value={lab.title} onChange={(v) => set('title', v)} placeholder="z. B. Cold E-Mail aufbauen" />
      </Field>
      <Field label="Briefing" hint="Die Aufgabenstellung: Was soll der Nutzer in dieser Übung tun?">
        <Area value={lab.brief} onChange={(v) => set('brief', v)} rows={3} placeholder="z. B. Schreibe eine kurze, personalisierte Cold E-Mail an …" />
      </Field>

      <JsonImport
        getExample={() => labExample(lab.mode || 'easy')}
        hint="Alternative zur KI: Schema kopieren → einer KI geben → ausgefülltes JSON einfügen oder als .json hochladen → importieren. Vorhandene Felder werden überschrieben, nicht angegebene bleiben erhalten."
        onImport={(data) => onChange({ ...lab, ...data })}
      />

      {/* Limits — only what the mode actually uses. */}
      {lab.mode === 'easy' && (
        <p className="aca-hint">Die Länge wird pro Abschnitt über das Wortlimit gesteuert (unten bei „Abschnitte"). Kein globales Limit nötig.</p>
      )}
      {lab.mode === 'hard' && (
        <div className="aca-row">
          <Field label="Wortlimit" hint="Maximale Länge der gesamten E-Mail (in Wörtern).">
            <Num value={lab.wordLimit} onChange={(v) => set('wordLimit', v)} />
          </Field>
        </div>
      )}
      {lab.mode === 'subject' && (
        <div className="aca-row">
          <Field label="Zeichenlimit pro Betreff" hint="Betreffzeilen werden in Zeichen gemessen (z. B. ~60), nicht in Wörtern.">
            <Num value={lab.charLimit} onChange={(v) => set('charLimit', v)} />
          </Field>
        </div>
      )}

      {/* Easy: email scaffold + blocks */}
      {lab.mode === 'easy' && (
        <>
          <Section title="E-Mail-Rahmen" defaultOpen={false}>
            <div className="aca-row">
              <Field label="Empfänger-Name"><Text value={(lab.email || {}).toName} onChange={(v) => setEmail('toName', v)} /></Field>
              <Field label="Empfänger-E-Mail"><Text value={(lab.email || {}).to} onChange={(v) => setEmail('to', v)} /></Field>
            </div>
            <Field label="Betreff-Platzhalter"><Text value={(lab.email || {}).subjectPlaceholder} onChange={(v) => setEmail('subjectPlaceholder', v)} /></Field>
            <Field label="Anrede"><Text value={(lab.email || {}).greeting} onChange={(v) => setEmail('greeting', v)} /></Field>
            <Field label="Grußformel"><Area value={(lab.email || {}).signoff} onChange={(v) => setEmail('signoff', v)} rows={2} /></Field>
            <Field label="Hinweis (rechte Spalte)"><Area value={(lab.email || {}).note} onChange={(v) => setEmail('note', v)} rows={2} /></Field>
          </Section>
          <Section title="Abschnitte (Blocks)" desc="Die festen Bausteine, die der Nutzer Schritt für Schritt ausfüllt." count={(lab.blocks || []).length}>
            <PresetRow
              presets={BLOCK_PRESETS}
              getKey={(p) => p.id}
              isUsed={(p) => (lab.blocks || []).some((b) => b.id === p.id)}
              onAdd={(p) => set('blocks', [...(lab.blocks || []), { ...p }])}
            />
            <ListEditor
              items={lab.blocks}
              onChange={(v) => set('blocks', v)}
              addLabel="Eigenen Abschnitt hinzufügen"
              blank={() => ({ id: '', label: '', hint: '', placeholder: '', wordLimit: 20 })}
              renderItem={(item, setItem) => (
                <>
                  <div className="aca-row">
                    <Field label="ID"><Text value={item.id} onChange={(v) => setItem({ ...item, id: slugify(v) })} placeholder="z. B. icebreaker" /></Field>
                    <Field label="Label"><Text value={item.label} onChange={(v) => setItem({ ...item, label: v })} /></Field>
                    <Field label="Wortlimit"><Num value={item.wordLimit} onChange={(v) => setItem({ ...item, wordLimit: v })} /></Field>
                  </div>
                  <Field label="Hinweis"><Text value={item.hint} onChange={(v) => setItem({ ...item, hint: v })} /></Field>
                  <Field label="Platzhalter"><Area value={item.placeholder} onChange={(v) => setItem({ ...item, placeholder: v })} rows={2} /></Field>
                </>
              )}
            />
          </Section>
        </>
      )}

      {/* Hard: structure */}
      {lab.mode === 'hard' && (
        <Section title="Psychologische Struktur" desc="Bausteine mit Gewichtung – als Orientierung für den Nutzer, der frei schreibt." count={(lab.structure || []).length}>
          <PresetRow
            presets={STRUCTURE_PRESETS}
            getKey={(p) => p.label}
            isUsed={(p) => (lab.structure || []).some((s) => s.label === p.label)}
            onAdd={(p) => set('structure', [...(lab.structure || []), { ...p }])}
          />
          <ListEditor
            items={lab.structure}
            onChange={(v) => set('structure', v)}
            addLabel="Eigenen Baustein hinzufügen"
            blank={() => ({ label: '', hint: '', words: 25 })}
            renderItem={(item, setItem) => (
              <div className="aca-row">
                <Field label="Label"><Text value={item.label} onChange={(v) => setItem({ ...item, label: v })} /></Field>
                <Field label="Hinweis"><Text value={item.hint} onChange={(v) => setItem({ ...item, hint: v })} /></Field>
                <Field label="Wörter"><Num value={item.words} onChange={(v) => setItem({ ...item, words: v })} /></Field>
              </div>
            )}
          />
        </Section>
      )}

      {/* Subject: angles — each angle is exactly one subject line to write. */}
      {lab.mode === 'subject' && (
        <Section title="Betreffzeilen (Angles)" desc="Jeder Angle ist genau eine Betreffzeile, die der Nutzer schreibt. So viele Angles = so viele Betreffzeilen." count={(lab.angles || []).length}>
          <PresetRow
            presets={ANGLE_PRESETS}
            getKey={(p) => p.name}
            isUsed={(p) => (lab.angles || []).some((a) => a.name === p.name)}
            onAdd={(p) => set('angles', [...(lab.angles || []), { ...p }])}
          />
          <ListEditor
            items={lab.angles}
            onChange={(v) => set('angles', v)}
            addLabel="Betreffzeile (Angle) hinzufügen"
            itemLabel="Betreffzeile"
            empty="Noch keine Betreffzeilen. Füge Angles hinzu – jeder wird eine Betreffzeile."
            blank={() => ({ name: '', hint: '', example: '' })}
            renderItem={(item, setItem) => (
              <>
                <div className="aca-row">
                  <Field label="Name"><Text value={item.name} onChange={(v) => setItem({ ...item, name: v })} /></Field>
                  <Field label="Beispiel"><Text value={item.example} onChange={(v) => setItem({ ...item, example: v })} /></Field>
                </div>
                <Field label="Hinweis"><Text value={item.hint} onChange={(v) => setItem({ ...item, hint: v })} /></Field>
              </>
            )}
          />
        </Section>
      )}

      {/* SalesGod: variables + prompts + starter */}
      {lab.mode === 'salesgod' && (
        <>
          <Section title="Datenbank-Variablen" desc="Platzhalter wie {{business_name}}, die der Nutzer im Template einsetzt." count={(lab.variables || []).length}>
            <PresetRow
              presets={VARIABLE_PRESETS}
              getKey={(p) => p.token}
              isUsed={(p) => (lab.variables || []).some((v) => v.token === p.token)}
              onAdd={(p) => set('variables', [...(lab.variables || []), { ...p }])}
            />
            <ListEditor
              items={lab.variables}
              onChange={(v) => set('variables', v)}
              addLabel="Eigene Variable hinzufügen"
              blank={() => ({ token: '', label: '', sample: '', special: false })}
              renderItem={(item, setItem) => (
                <>
                  <div className="aca-row">
                    <Field label="Token"><Text value={item.token} onChange={(v) => setItem({ ...item, token: slugify(v).replace(/-/g, '_') })} placeholder="business_name" /></Field>
                    <Field label="Label"><Text value={item.label} onChange={(v) => setItem({ ...item, label: v })} /></Field>
                    <Field label="Beispielwert"><Text value={item.sample} onChange={(v) => setItem({ ...item, sample: v })} /></Field>
                  </div>
                  <label className="aca-check">
                    <input type="checkbox" checked={!!item.special} onChange={(e) => setItem({ ...item, special: e.target.checked })} />
                    Spezial-Snippet (z. B. greeting)
                  </label>
                </>
              )}
            />
          </Section>
          <Section title="KI-Prompt-Ideen" defaultOpen={false}>
            <StringList items={lab.promptExamples} onChange={(v) => set('promptExamples', v)} addLabel="Prompt-Idee hinzufügen" placeholder="ein Bezug zu einem aktuellen Projekt" />
          </Section>
          <Field label="Starter-Template"><Area value={lab.starter} onChange={(v) => set('starter', v)} rows={3} placeholder="{{greeting}}" /></Field>
        </>
      )}

      {/* Context (all modes) */}
      <Section title="Kontext zum Arbeiten" desc="Infos, die dem Nutzer während der Übung angezeigt werden – damit er weiß, an wen und worüber er schreibt." defaultOpen={false}>
        <Field label="Szenario" hint="Die Ausgangslage: Wer ist der Empfänger, was bietest du an?">
          <Area value={ctx.scenario} onChange={(v) => setContext('scenario', v)} rows={3} placeholder="z. B. Du schreibst an die Marketingleiterin einer mittelgroßen SaaS-Firma …" />
        </Field>
        <div className="aca-sub-label">Fakten</div>
        <ListEditor
          items={ctx.facts}
          onChange={(v) => setContext('facts', v)}
          addLabel="Fakt hinzufügen"
          blank={() => ({ label: '', value: '' })}
          renderItem={(item, setItem) => (
            <div className="aca-row">
              <Field label="Label"><Text value={item.label} onChange={(v) => setItem({ ...item, label: v })} /></Field>
              <Field label="Wert"><Text value={item.value} onChange={(v) => setItem({ ...item, value: v })} /></Field>
            </div>
          )}
        />
        <div className="aca-sub-label">Notizen</div>
        <StringList items={ctx.notes} onChange={(v) => setContext('notes', v)} addLabel="Notiz hinzufügen" placeholder="Hinweis für den User" />
      </Section>

      <button type="button" className="aca-btn aca-btn-danger aca-btn-sm" onClick={() => onChange(null)}>
        Practice Lab entfernen
      </button>
    </div>
  );
};

/* ── Quiz editor ─────────────────────────────────────────────── */
const QTYPES = [
  { value: 'multiple-choice', label: 'Multiple Choice' },
  { value: 'text', label: 'Freitext' },
  { value: 'email', label: 'E-Mail (Betreff + Text)' },
];

const QuizEditor = ({ value, onChange }) => {
  if (!value) {
    return (
      <div className="aca-lab-empty">
        <button type="button" className="aca-add-row" onClick={() => onChange({ questions: [] })}>
          <PlusIcon /> Mini-Quiz hinzufügen
        </button>
        <JsonImport
          getExample={quizExample}
          requireArrayKey="questions"
          hint="Schema kopieren → einer KI geben → ausgefülltes JSON einfügen oder als .json hochladen → importieren. Erstellt direkt das Quiz mit allen Fragen."
          onImport={(data) => onChange({ questions: data.questions })}
        />
      </div>
    );
  }
  const questions = value.questions || [];
  const setQuestions = (q) => onChange({ ...value, questions: q });
  return (
    <div className="aca-sub">
      <JsonImport
        getExample={quizExample}
        requireArrayKey="questions"
        hint="Schema kopieren → einer KI geben → ausgefülltes JSON einfügen oder als .json hochladen → importieren. Ersetzt alle bestehenden Fragen."
        onImport={(data) => onChange({ ...value, questions: data.questions })}
      />
      <ListEditor
        items={questions}
        onChange={setQuestions}
        addLabel="Frage hinzufügen"
        blank={() => ({ id: `q${Date.now().toString(36)}`, type: 'multiple-choice', question: '', options: ['', ''], correctIndex: 0 })}
        renderItem={(q, setQ) => (
          <>
            <div className="aca-row">
              <Field label="Typ"><Select value={q.type} onChange={(v) => setQ({ ...q, type: v })} options={QTYPES} /></Field>
            </div>
            <Field label="Frage"><Area value={q.question} onChange={(v) => setQ({ ...q, question: v })} rows={2} /></Field>
            {q.type === 'multiple-choice' && (
              <>
                <div className="aca-sub-label">Antwortoptionen (Richtige markieren)</div>
                {(q.options || []).map((opt, oi) => (
                  <div className="aca-option-row" key={oi}>
                    <input type="radio" name={`correct-${q.id}`} checked={q.correctIndex === oi} onChange={() => setQ({ ...q, correctIndex: oi })} />
                    <Text value={opt} onChange={(v) => setQ({ ...q, options: q.options.map((o, idx) => (idx === oi ? v : o)) })} placeholder={`Option ${oi + 1}`} />
                    <button type="button" className="aca-icon-btn is-danger" onClick={() => setQ({ ...q, options: q.options.filter((_, idx) => idx !== oi), correctIndex: 0 })}><TrashIcon /></button>
                  </div>
                ))}
                <button type="button" className="aca-add-row aca-add-row-sm" onClick={() => setQ({ ...q, options: [...(q.options || []), ''] })}><PlusIcon /> Option</button>
              </>
            )}
            {q.type === 'text' && (
              <Field label="Platzhalter"><Text value={q.placeholder} onChange={(v) => setQ({ ...q, placeholder: v })} /></Field>
            )}
            {q.type === 'email' && (
              <div className="aca-row">
                <Field label="Betreff-Platzhalter"><Text value={q.subjectPlaceholder} onChange={(v) => setQ({ ...q, subjectPlaceholder: v })} /></Field>
                <Field label="Text-Platzhalter"><Text value={q.bodyPlaceholder} onChange={(v) => setQ({ ...q, bodyPlaceholder: v })} /></Field>
              </div>
            )}
          </>
        )}
      />
      <button type="button" className="aca-btn aca-btn-danger aca-btn-sm" onClick={() => onChange(null)}>Quiz entfernen</button>
    </div>
  );
};

/* ── Topic (Themengebiet) form ───────────────────────────────── */
const TrackForm = ({ topic, registerStatus }) => {
  const [draft, setDraft] = useState(() => topic);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const status = useAutoSave(draft, async (d) => {
    const { id, courses, ...rest } = d;
    await writeTopic(id, rest);
  });
  useEffect(() => { registerStatus && registerStatus(status); }, [status, registerStatus]);

  const accent = draft.accent || '#6da7ed';
  const courseCount = (topic.courses || []).length;
  const lessonCount = (topic.courses || []).reduce((n, c) => n + (c.lessons || []).length, 0);

  return (
    <div className="aca-form-split">
      <div className="aca-blocks">
        <BlockCard title="Grunddaten" desc="Name, Slogan und Beschreibung – erscheinen auf der Academy-Übersicht." icon={<IconTrack />}>
          <Field label="Titel"><Text value={draft.title} onChange={(v) => set('title', v)} placeholder="z. B. Cold Outreach Mastery" /></Field>
          <Field label="Tagline" hint="Kurzer Slogan, der unter dem Titel angezeigt wird."><Text value={draft.tagline} onChange={(v) => set('tagline', v)} placeholder="z. B. Cold to booked." /></Field>
          <Field label="Beschreibung"><Area value={draft.description} onChange={(v) => set('description', v)} rows={4} /></Field>
        </BlockCard>

        <BlockCard title="Darstellung" desc="Coverbild und Akzentfarbe prägen das Erscheinungsbild der Kacheln." icon={<IconImage />}>
          <Field label="Coverbild" hint="Empfohlen: 16:9, mind. 1280 × 720 px."><ImageUploader value={draft.image} onChange={(v) => set('image', v)} scope="topics" /></Field>
          <Field label="Akzentfarbe">
            <div className="aca-color-row">
              <input className="aca-color" type="color" value={accent} onChange={(e) => set('accent', e.target.value)} />
              <Text value={accent} onChange={(v) => set('accent', v)} placeholder="#6da7ed" />
            </div>
          </Field>
        </BlockCard>

        <BlockCard title="Value Vault" desc="Downloads & Material zum ganzen Themengebiet (PDFs, Vorlagen, Dokumente)." icon={<IconFile />}>
          <ListEditor
            items={draft.valueVault}
            onChange={(v) => set('valueVault', v)}
            addLabel="Ressource hinzufügen"
            itemLabel="Ressource"
            empty="Noch keine Ressourcen. Lade PDFs, Vorlagen oder Dokumente hoch."
            blank={() => ({ title: '', type: 'doc', url: '' })}
            renderItem={(item, setItem) => (
              <>
                <Field label="Titel"><Text value={item.title} onChange={(v) => setItem({ ...item, title: v })} placeholder="z. B. Cold-Email-Vorlage" /></Field>
                <Field label="Datei">
                  <FileUploader
                    value={item}
                    scope="resources"
                    onChange={(meta) => meta
                      ? setItem({ ...item, url: meta.url, type: meta.type, name: meta.name, path: meta.path, title: item.title || baseName(meta.name) })
                      : setItem({ ...item, url: '', name: '', path: '' })}
                  />
                </Field>
              </>
            )}
          />
        </BlockCard>
      </div>

      <aside className="aca-preview">
        <span className="aca-preview-label">Live-Vorschau</span>
        <div className="aca-tcard is-preview" style={{ '--aca-accent': accent }}>
          <div className="aca-tcard-cover">
            {draft.image ? <img src={draft.image} alt="" /> : <span className="aca-tcard-cover-fallback"><IconTrack /></span>}
          </div>
          <div className="aca-tcard-body">
            {draft.tagline && <span className="aca-tcard-tag">{draft.tagline}</span>}
            <span className="aca-tcard-title">{draft.title || 'Titel des Themengebiets'}</span>
            <span className="aca-tcard-meta">{courseCount} Kurse · {lessonCount} Lektionen</span>
          </div>
        </div>
        <p className="aca-preview-hint">So erscheint das Themengebiet auf der Academy-Startseite.</p>
      </aside>
    </div>
  );
};

/* ── Course form ─────────────────────────────────────────────── */
const CourseForm = ({ topicId, course, accent, registerStatus }) => {
  const [draft, setDraft] = useState(() => course);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setBadge = (k, v) => setDraft((d) => ({ ...d, badge: { ...(d.badge || {}), [k]: v } }));
  const status = useAutoSave(draft, async (d) => {
    const { id, lessons, ...rest } = d;
    await writeCourse(topicId, id, rest);
  });
  useEffect(() => { registerStatus && registerStatus(status); }, [status, registerStatus]);

  const lessonCount = (course.lessons || []).length;
  const accentColor = accent || '#6da7ed';

  return (
    <div className="aca-form-split">
      <div className="aca-blocks">
        <BlockCard title="Grunddaten" desc="Titel und Beschreibung des Kurses. Der Kurs bündelt mehrere Lektionen." icon={<IconCourse />}>
          <Field label="Titel"><Text value={draft.title} onChange={(v) => set('title', v)} placeholder="z. B. Die perfekte Cold E-Mail" /></Field>
          <Field label="Untertitel"><Text value={draft.subtitle} onChange={(v) => set('subtitle', v)} placeholder="z. B. In 5 Schritten zur Antwort" /></Field>
          <Field label="Beschreibung"><Area value={draft.description} onChange={(v) => set('description', v)} rows={4} /></Field>
        </BlockCard>

        <BlockCard title="Darstellung" desc="Eigenes Titelbild des Kurses. Ohne Bild wird das Vorschaubild der ersten Lektion verwendet." icon={<IconImage />}>
          <Field label="Titelbild" hint="Empfohlen: 16:9, mind. 1280 × 720 px."><ImageUploader value={draft.cover} onChange={(v) => set('cover', v)} scope="courses" /></Field>
        </BlockCard>

        <BlockCard title="Abzeichen (Badge)" desc="Wird dem User vergeben, sobald er alle Lektionen dieses Kurses abgeschlossen hat." icon={<IconLab />}>
          <div className="aca-row">
            <Field label="Badge-ID" hint="Verknüpft Design & Eigenschaften."><Text value={(draft.badge || {}).id} onChange={(v) => setBadge('id', slugify(v))} placeholder="z. B. cold-email-rookie" /></Field>
            <Field label="Badge-Name"><Text value={(draft.badge || {}).name} onChange={(v) => setBadge('name', v)} placeholder="z. B. Cold Email Rookie" /></Field>
          </div>
        </BlockCard>
      </div>

      <aside className="aca-preview">
        <span className="aca-preview-label">Live-Vorschau</span>
        <div className="aca-tcard is-preview" style={{ '--aca-accent': accentColor }}>
          <div className="aca-tcard-cover">
            {draft.cover ? <img src={draft.cover} alt="" /> : <span className="aca-tcard-cover-fallback"><IconCourse /></span>}
          </div>
          <div className="aca-tcard-body">
            {draft.subtitle && <span className="aca-tcard-tag">{draft.subtitle}</span>}
            <span className="aca-tcard-title">{draft.title || 'Kurstitel'}</span>
            <span className="aca-tcard-meta">{lessonCount} Lektionen{(draft.badge || {}).name ? ` · ${draft.badge.name}` : ''}</span>
          </div>
        </div>
        <p className="aca-preview-hint">So erscheint der Kurs in der Kursliste des Themengebiets.</p>
      </aside>
    </div>
  );
};

/* ── Course path (frontend-mirrored step timeline) ───────────── */
/* One step per lesson, by its type (matches the frontend getCourseSteps). */
const STEP_META = {
  video: { label: 'Video', color: '#f078a8', icon: <IconVideo /> },
  practice: { label: 'Practice Lab', color: '#5fcdd0', icon: <IconLab /> },
  quiz: { label: 'Mini-Quiz', color: '#f0b85f', icon: <IconQuiz /> },
};

const CoursePath = ({ topic, course, onSelectLesson, onMove }) => {
  const accent = topic.accent || '#6da7ed';
  const badge = course.badge && course.badge.name ? course.badge : null;
  const lastIndex = course.lessons.length - 1;

  return (
    <div className="aca-path">
      {course.lessons.map((lesson, li) => {
        const type = resolveLessonType(lesson);
        const meta = STEP_META[type];
        const lab = lesson.practiceLab;
        const qCount = lesson.quiz ? (lesson.quiz.questions || []).length : 0;
        const open = () => onSelectLesson(lesson.id);
        return (
          <div
            key={lesson.id}
            className="aca-path-node is-clickable"
            style={{ '--node-color': meta.color }}
            role="button"
            tabIndex={0}
            onClick={open}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
          >
            <span className="aca-path-rail"><span className="aca-path-dot">{meta.icon}</span><span className="aca-path-line" /></span>
            <span className="aca-path-card">
              <span className="aca-path-kicker">Lektion {li + 1} · {meta.label}</span>
              <span className="aca-path-title">{lesson.title || 'Unbenannte Lektion'}</span>
              {type === 'practice' && lab?.mode && <span className="aca-path-meta">Modus: {lab.mode}</span>}
              {type === 'quiz' && <span className="aca-path-meta">{qCount} {qCount === 1 ? 'Frage' : 'Fragen'}</span>}
              {type === 'video' && !lesson.video?.url && <span className="aca-path-meta is-warn">Noch kein Video hinterlegt</span>}
            </span>
            {onMove && (
              <span className="aca-path-move" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="aca-icon-btn" title="Nach oben" disabled={li === 0} onClick={() => onMove(li, -1)}>↑</button>
                <button type="button" className="aca-icon-btn" title="Nach unten" disabled={li === lastIndex} onClick={() => onMove(li, 1)}>↓</button>
              </span>
            )}
            <span className="aca-path-arrow"><Chevron /></span>
          </div>
        );
      })}

      <div className="aca-path-node is-result" style={{ '--node-color': badge ? accent : '#6f6f7a' }}>
        <span className="aca-path-rail"><span className="aca-path-dot"><IconTrophy /></span></span>
        <span className="aca-path-card">
          <span className="aca-path-kicker">Abschluss</span>
          <span className="aca-path-title">{badge ? `Abzeichen: ${badge.name}` : 'Kein Abzeichen festgelegt'}</span>
          <span className="aca-path-meta">{badge ? 'Wird vergeben, sobald alle Schritte erledigt sind.' : 'Lege oben im Kurs ein Abzeichen fest.'}</span>
        </span>
      </div>
    </div>
  );
};

/* ── Lesson form ─────────────────────────────────────────────── */
const LessonForm = ({ topicId, courseId, lesson, accent, registerStatus }) => {
  const type = resolveLessonType(lesson);
  const [draft, setDraft] = useState(() => ({ ...lesson, type }));
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setVideo = (k, v) => setDraft((d) => ({ ...d, video: { ...(d.video || {}), [k]: v } }));
  const status = useAutoSave(draft, async (d) => {
    const { id, ...rest } = d;
    await writeLesson(topicId, courseId, id, { ...rest });
  });
  useEffect(() => { registerStatus && registerStatus(status); }, [status, registerStatus]);

  const meta = STEP_META[type];
  const video = draft.video || {};
  const accentColor = accent || '#6da7ed';

  return (
    <div className="aca-form">
      <div className="aca-form-split">
        <div className="aca-blocks">
          <BlockCard title="Grunddaten" desc="Titel, Kicker-Label und kurze Beschreibung dieser Lektion." icon={<IconLesson />}>
            <div className="aca-row">
              <Field label="Titel"><Text value={draft.title} onChange={(v) => set('title', v)} placeholder="z. B. Der erste Satz entscheidet" /></Field>
              <Field label="Label (Kicker)"><Text value={draft.label} onChange={(v) => set('label', v)} placeholder="z. B. WOCHE 1" /></Field>
            </div>
            <Field label="Beschreibung"><Area value={draft.description} onChange={(v) => set('description', v)} rows={3} /></Field>
          </BlockCard>

          {type === 'video' && (
            <>
              <BlockCard title="Video" desc="Hochladen → wird in Firebase Storage gespeichert. Oder externe URL eintragen." icon={<IconVideo />}>
                <div className="aca-row">
                  <Field label="Quelle"><Select value={video.provider || 'bunny'} onChange={(v) => setVideo('provider', v)} options={[{ value: 'bunny', label: 'Upload → Storage' }, { value: 'url', label: 'Externe URL' }]} /></Field>
                  <Field label="Anzeige-Dauer" hint="Wird beim Upload automatisch erkannt – manuell überschreibbar."><Text value={draft.duration} onChange={(v) => set('duration', v)} placeholder="z. B. 12:30" /></Field>
                </div>
                {(video.provider || 'bunny') === 'bunny' ? (
                  <Field label="Video-Datei" hint="Wird in Firebase Storage gespeichert und direkt von dort ausgeliefert.">
                    <VideoUploader
                      value={video.url}
                      onChange={(v) => setVideo('url', v)}
                      onPathChange={(p) => setVideo('bunnyPath', p)}
                      onMeta={(m) => { set('durationSeconds', m.seconds); set('duration', m.label); }}
                    />
                  </Field>
                ) : (
                  <Field label="Video-URL"><Text value={video.url} onChange={(v) => setVideo('url', v)} /></Field>
                )}
                <Field label="Vorschaubild (Poster)"><ImageUploader value={draft.preview} onChange={(v) => set('preview', v)} scope="lessons" /></Field>
              </BlockCard>

              <BlockCard title="Cheatsheets / Downloads" desc="Material zum Herunterladen für diese Lektion." icon={<IconFile />}>
                <ListEditor
                  items={draft.cheatsheets}
                  onChange={(v) => set('cheatsheets', v)}
                  addLabel="Cheatsheet hinzufügen"
                  itemLabel="Cheatsheet"
                  empty="Noch keine Cheatsheets. Lade ein PDF oder Dokument hoch."
                  blank={() => ({ title: '', type: 'pdf', url: '' })}
                  renderItem={(item, setItem) => (
                    <>
                      <Field label="Titel"><Text value={item.title} onChange={(v) => setItem({ ...item, title: v })} placeholder="z. B. Einwand-Handbuch" /></Field>
                      <Field label="Datei">
                        <FileUploader
                          value={item}
                          scope="cheatsheets"
                          onChange={(m) => m
                            ? setItem({ ...item, url: m.url, type: m.type, name: m.name, path: m.path, title: item.title || baseName(m.name) })
                            : setItem({ ...item, url: '', name: '', path: '' })}
                        />
                      </Field>
                    </>
                  )}
                />
              </BlockCard>
            </>
          )}

          {type === 'quiz' && (
            <BlockCard title="Mini-Quiz" desc="Die Fragen, die der User in dieser Lektion beantwortet." icon={<IconQuiz />}>
              <QuizEditor value={draft.quiz} onChange={(v) => set('quiz', v)} />
            </BlockCard>
          )}

          {type === 'practice' && (
            <BlockCard title="Practice Lab" desc="Die interaktive Schreibübung dieser Lektion." icon={<IconLab />}>
              <PracticeLabEditor
                value={draft.practiceLab}
                onChange={(v) => set('practiceLab', v)}
                onLessonMeta={(m) => setDraft((d) => {
                  const next = { ...d };
                  if (m.title) next.title = m.title;
                  if (m.label) next.label = m.label;
                  if (m.description) next.description = m.description;
                  return next;
                })}
              />
            </BlockCard>
          )}
        </div>

        <aside className="aca-preview">
          <span className="aca-preview-label">Live-Vorschau</span>
          <div className="aca-ccard is-preview" style={{ '--aca-accent': meta.color }}>
            <span className="aca-ccard-icon">{meta.icon}</span>
            {draft.label && <span className="aca-tcard-tag">{draft.label}</span>}
            <span className="aca-ccard-title">{draft.title || 'Lektionstitel'}</span>
            <span className="aca-ccard-tags">
              <span className="aca-tag">{meta.icon} {meta.label}</span>
            </span>
          </div>
          <p className="aca-preview-hint">Diese Lektion ist ein <strong>{meta.label}</strong>-Schritt im Lernpfad.</p>
        </aside>
      </div>
    </div>
  );
};

/* ── Sidebar tree row ────────────────────────────────────────── */
const TreeRow = ({ depth = 0, active, expandable, expanded, onToggle, onSelect, icon, label, meta, accent }) => (
  <div
    className={`aca-tree-row ${active ? 'is-active' : ''}`}
    style={{ paddingLeft: 8 + depth * 15, '--row-accent': accent || 'var(--aca-accent)' }}
  >
    {expandable ? (
      <button type="button" className="aca-tree-caret" onClick={onToggle} aria-label="Aufklappen">
        <Chevron dir={expanded ? 'down' : 'right'} />
      </button>
    ) : (
      <span className="aca-tree-caret aca-tree-caret-empty" />
    )}
    <button type="button" className="aca-tree-label" onClick={onSelect}>
      <span className="aca-tree-icon">{icon}</span>
      <span className="aca-tree-text">{label || 'Ohne Titel'}</span>
      {meta != null && meta !== '' && <span className="aca-tree-meta">{meta}</span>}
    </button>
  </div>
);

/* ── Overview stat tile ──────────────────────────────────────── */
const StatCard = ({ icon, value, label, tint }) => (
  <div className="aca-stat" style={tint ? { '--stat-tint': tint } : undefined}>
    <span className="aca-stat-icon">{icon}</span>
    <span className="aca-stat-value">{value}</span>
    <span className="aca-stat-label">{label}</span>
  </div>
);

/* ── Editor hero header (cover / accent gradient + hierarchy) ──── */
const EditorHeader = ({ kind, title, subtitle, accent, id, parent, stats, cover, icon, status, onDelete, deleteLabel }) => (
  <div className={`aca-hero ${cover ? 'has-cover' : ''}`} style={{ '--hero-accent': accent || '#6da7ed' }}>
    {cover && <div className="aca-hero-bg" style={{ backgroundImage: `url(${cover})` }} />}
    <div className="aca-hero-glow" />
    <div className="aca-hero-inner">
      <div className="aca-hero-toprow">
        <div className="aca-ed-top">
          <span className="aca-ed-kind">{icon}{kind}</span>
          {parent && <span className="aca-ed-parent">{parent}</span>}
          {id && <span className="aca-ed-id">/{id}</span>}
        </div>
        <div className="aca-hero-actions">
          {status && <SaveStatus status={status} />}
          {onDelete && (
            <button type="button" className="aca-hero-del" onClick={onDelete} title={deleteLabel}>
              <TrashIcon /> {deleteLabel}
            </button>
          )}
        </div>
      </div>
      <h1 className="aca-ed-title">{title || 'Ohne Titel'}</h1>
      {subtitle && <p className="aca-ed-sub">{subtitle}</p>}
      {stats && stats.length > 0 && (
        <div className="aca-ed-stats">
          {stats.map((s) => (
            <span className="aca-ed-stat" key={s.label}><b>{s.value}</b> {s.label}</span>
          ))}
        </div>
      )}
    </div>
  </div>
);

/* ── Name modal (create track / course / lesson) ─────────────── */
const NameModal = ({ data, onClose }) => {
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const submit = (e) => {
    e.preventDefault();
    const v = value.trim();
    if (!v) return;
    data.onConfirm(v);
    onClose();
  };

  return (
    <div className="aca-modal-overlay" onClick={onClose} role="presentation">
      <form className="aca-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="aca-modal-head">
          <span className="aca-modal-eyebrow">{data.eyebrow}</span>
          <h3 className="aca-modal-title">{data.title}</h3>
        </div>
        <label className="aca-field">
          <span className="aca-field-label">{data.fieldLabel || 'Name'}</span>
          <input
            ref={inputRef}
            className="aca-input"
            value={value}
            placeholder={data.placeholder}
            onChange={(e) => setValue(e.target.value)}
          />
        </label>
        <div className="aca-modal-actions">
          <button type="button" className="aca-btn aca-btn-ghost" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="aca-btn aca-btn-primary" disabled={!value.trim()}>
            {data.confirmLabel || 'Erstellen'}
          </button>
        </div>
      </form>
    </div>
  );
};

/* Confirm dialog for destructive actions (replaces window.confirm). */
const ConfirmModal = ({ data, onClose }) => {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const confirm = async () => {
    setBusy(true);
    try { await data.onConfirm(); onClose(); }
    finally { setBusy(false); }
  };

  return (
    <div className="aca-modal-overlay" onClick={onClose} role="presentation">
      <div className="aca-modal aca-modal-danger" onClick={(e) => e.stopPropagation()}>
        <div className="aca-modal-head">
          <span className="aca-modal-iconwrap"><TrashIcon /></span>
          <span className="aca-modal-eyebrow">{data.eyebrow || 'Löschen'}</span>
          <h3 className="aca-modal-title">{data.title}</h3>
          {data.message && <p className="aca-modal-text">{data.message}</p>}
        </div>
        <div className="aca-modal-actions">
          <button type="button" className="aca-btn aca-btn-ghost" onClick={onClose} disabled={busy}>Abbrechen</button>
          <button type="button" className="aca-btn aca-btn-danger" onClick={confirm} disabled={busy}>
            {busy ? 'Löscht…' : (data.confirmLabel || 'Endgültig löschen')}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main component ──────────────────────────────────────────── */
const AcademyAdmin = () => {
  const [academy, setAcademy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nav, setNav] = useState({ topicId: null, courseId: null, lessonId: null });
  const [expandedTopics, setExpandedTopics] = useState([]);
  const [expandedCourses, setExpandedCourses] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [lessonModal, setLessonModal] = useState(null);
  const [editStatus, setEditStatus] = useState('saved');

  useEffect(() => subscribeAcademy((data) => { setAcademy(data); setLoading(false); }), []);

  const topics = academy?.topics || [];
  const topic = topics.find((t) => t.id === nav.topicId) || null;
  const course = topic?.courses.find((c) => c.id === nav.courseId) || null;
  const lesson = course?.lessons.find((l) => l.id === nav.lessonId) || null;

  const stats = React.useMemo(() => {
    let courses = 0; let lessons = 0; let quizzes = 0; let labs = 0; let videos = 0;
    topics.forEach((t) => (t.courses || []).forEach((c) => {
      courses += 1;
      (c.lessons || []).forEach((l) => {
        lessons += 1;
        if (l.quiz) quizzes += 1;
        if (l.practiceLab) labs += 1;
        if (l.video?.url) videos += 1;
      });
    }));
    return { tracks: topics.length, courses, lessons, quizzes, labs, videos };
  }, [topics]);

  const toggle = (setter) => (id) => setter((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const toggleTopic = toggle(setExpandedTopics);
  const toggleCourse = toggle(setExpandedCourses);
  const ensure = (setter) => (id) => setter((cur) => (cur.includes(id) ? cur : [...cur, id]));
  const expandTopic = ensure(setExpandedTopics);
  const expandCourse = ensure(setExpandedCourses);

  const goRoot = () => setNav({ topicId: null, courseId: null, lessonId: null });
  const selectTopic = (id) => { setNav({ topicId: id, courseId: null, lessonId: null }); expandTopic(id); };
  const selectCourse = (tId, cId) => { setNav({ topicId: tId, courseId: cId, lessonId: null }); expandTopic(tId); expandCourse(cId); };
  const selectLesson = (tId, cId, lId) => { setNav({ topicId: tId, courseId: cId, lessonId: lId }); expandTopic(tId); expandCourse(cId); };

  const handleSeed = async () => {
    if (!window.confirm('Aktuelle Demo-Inhalte in die Datenbank übertragen?')) return;
    setSeeding(true);
    try { await seedAcademy({ overwrite: false }); }
    catch (e) { window.alert(e.message); }
    finally { setSeeding(false); }
  };

  const createTopic = () => setModal({
    eyebrow: 'Themengebiet',
    title: 'Neues Themengebiet anlegen',
    fieldLabel: 'Titel des Themengebiets',
    placeholder: 'z. B. Cold Outreach',
    confirmLabel: 'Themengebiet erstellen',
    onConfirm: async (title) => {
      const id = uniqueId(title, topics.map((t) => t.id));
      await writeTopic(id, { title, tagline: '', description: '', accent: '#6da7ed', image: '', order: topics.length });
      selectTopic(id);
    },
  });
  const createCourse = (tp) => {
    if (!tp) return;
    setModal({
      eyebrow: `Themengebiet · ${tp.title}`,
      title: 'Neuen Kurs anlegen',
      fieldLabel: 'Titel des Kurses',
      placeholder: 'z. B. Die perfekte Cold E-Mail',
      confirmLabel: 'Kurs erstellen',
      onConfirm: async (title) => {
        const id = uniqueId(title, tp.courses.map((c) => c.id));
        await writeCourse(tp.id, id, { title, subtitle: '', description: '', order: tp.courses.length });
        selectCourse(tp.id, id);
      },
    });
  };
  const createLesson = (tp, cs) => {
    if (!tp || !cs) return;
    setLessonModal({
      onCreate: async (type) => {
        const id = uniqueId('lektion', cs.lessons.map((l) => l.id));
        const content = (LESSON_TYPES.find((t) => t.id === type) || LESSON_TYPES[0]).build();
        await writeLesson(tp.id, cs.id, id, {
          title: '', label: '', description: '',
          ...content, order: cs.lessons.length,
        });
        selectLesson(tp.id, cs.id, id);
      },
    });
  };
  // Move a lesson up (-1) or down (+1) in the course path and persist the new
  // order. `course.lessons` is already sorted, so we just swap and rewrite
  // 0..n-1 in one atomic update.
  const moveLesson = async (tp, cs, index, dir) => {
    if (!tp || !cs) return;
    const lessons = cs.lessons || [];
    const target = index + dir;
    if (target < 0 || target >= lessons.length) return;
    const ids = lessons.map((l) => l.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    await reorderLessons(tp.id, cs.id, ids);
  };

  const deleteTopic = (tp) => setConfirm({
    eyebrow: 'Themengebiet löschen',
    title: `„${tp.title}" löschen?`,
    message: 'Alle Kurse und Lektionen dieses Themengebiets werden unwiderruflich entfernt.',
    onConfirm: async () => { await removeTopic(tp.id); goRoot(); },
  });
  const deleteCourse = (tp, cs) => setConfirm({
    eyebrow: 'Kurs löschen',
    title: `„${cs.title}" löschen?`,
    message: 'Alle Lektionen dieses Kurses werden unwiderruflich entfernt.',
    onConfirm: async () => { await removeCourse(tp.id, cs.id); selectTopic(tp.id); },
  });
  const deleteLesson = (tp, cs, ls) => setConfirm({
    eyebrow: 'Lektion löschen',
    title: `„${ls.title}" löschen?`,
    message: 'Diese Lektion mit allen Inhalten (Video, Quiz, Practice Lab) wird unwiderruflich entfernt.',
    onConfirm: async () => { await removeLesson(tp.id, cs.id, ls.id); selectCourse(tp.id, cs.id); },
  });

  if (loading) return <div className="aca-app"><div className="aca-loading">Lädt…</div></div>;

  return (
    <div className="aca-app">
      {/* ── Sidebar tree ── */}
      <aside className="aca-sidebar">
        <div className="aca-sb-head">
          <span className="aca-sb-title">Inhalte</span>
          <button type="button" className="aca-sb-add" onClick={createTopic} title="Neues Themengebiet"><PlusIcon /></button>
        </div>
        <div className="aca-tree">
          {topics.length === 0 && (
            <p className="aca-tree-empty">Noch keine Inhalte.</p>
          )}
          {topics.map((t) => {
            const tExpanded = expandedTopics.includes(t.id);
            return (
              <div className="aca-tree-group" key={t.id}>
                <TreeRow
                  depth={0}
                  accent={t.accent}
                  active={nav.topicId === t.id && !nav.courseId}
                  expandable={(t.courses || []).length > 0}
                  expanded={tExpanded}
                  onToggle={() => toggleTopic(t.id)}
                  onSelect={() => selectTopic(t.id)}
                  icon={<IconTrack />}
                  label={t.title}
                  meta={(t.courses || []).length || null}
                />
                {tExpanded && (t.courses || []).map((c) => {
                  const cExpanded = expandedCourses.includes(c.id);
                  return (
                    <div key={c.id}>
                      <TreeRow
                        depth={1}
                        active={nav.courseId === c.id && !nav.lessonId}
                        expandable={(c.lessons || []).length > 0}
                        expanded={cExpanded}
                        onToggle={() => toggleCourse(c.id)}
                        onSelect={() => selectCourse(t.id, c.id)}
                        icon={<IconCourse />}
                        label={c.title}
                        meta={(c.lessons || []).length || null}
                      />
                      {cExpanded && (c.lessons || []).map((l) => (
                        <TreeRow
                          key={l.id}
                          depth={2}
                          active={nav.lessonId === l.id}
                          expandable={false}
                          onSelect={() => selectLesson(t.id, c.id, l.id)}
                          icon={(STEP_META[resolveLessonType(l)] || {}).icon || <IconLesson />}
                          label={l.title || 'Unbenannte Lektion'}
                        />
                      ))}
                      {cExpanded && (
                        <button type="button" className="aca-tree-add" style={{ paddingLeft: 38 }} onClick={() => createLesson(t, c)}>
                          <PlusIcon /> Lektion
                        </button>
                      )}
                    </div>
                  );
                })}
                {tExpanded && (
                  <button type="button" className="aca-tree-add" style={{ paddingLeft: 23 }} onClick={() => createCourse(t)}>
                    <PlusIcon /> Kurs
                  </button>
                )}
              </div>
            );
          })}
          <button type="button" className="aca-tree-add aca-tree-add-root" onClick={createTopic}>
            <PlusIcon /> Neues Themengebiet
          </button>
        </div>
      </aside>

      {/* ── Main panel ── */}
      <main className="aca-main">
        <header className="aca-main-head">
          <div className="aca-breadcrumb">
            <button className="aca-crumb" onClick={goRoot}>Academy</button>
            {topic && <><span className="aca-crumb-sep"><Chevron /></span><button className="aca-crumb" onClick={() => selectTopic(topic.id)}>{topic.title}</button></>}
            {course && <><span className="aca-crumb-sep"><Chevron /></span><button className="aca-crumb" onClick={() => selectCourse(topic.id, course.id)}>{course.title}</button></>}
            {lesson && <><span className="aca-crumb-sep"><Chevron /></span><span className="aca-crumb is-current">{lesson.title}</span></>}
          </div>
        </header>

        <div className="aca-main-body">
          {/* Root → dashboard overview */}
          {!topic && (
            <div className="aca-overview">
              <div className="aca-overview-head">
                <div>
                  <h1 className="aca-overview-title">Academy verwalten</h1>
                  <p className="aca-overview-sub">Themengebiete, Kurse, Lektionen, Videos, Quizze & Practice Labs – alles an einem Ort.</p>
                </div>
                {academy?.isEmpty && (
                  <button className="aca-btn aca-btn-ghost" onClick={handleSeed} disabled={seeding}>
                    {seeding ? 'Überträgt…' : 'Demo-Inhalte importieren'}
                  </button>
                )}
              </div>

              <div className="aca-stats">
                <StatCard icon={<IconTrack />} value={stats.tracks} label="Themengebiete" tint="#6da7ed" />
                <StatCard icon={<IconCourse />} value={stats.courses} label="Kurse" tint="#a98cf0" />
                <StatCard icon={<IconLesson />} value={stats.lessons} label="Lektionen" tint="#5fd0a8" />
                <StatCard icon={<IconVideo />} value={stats.videos} label="Videos" tint="#f078a8" />
                <StatCard icon={<IconQuiz />} value={stats.quizzes} label="Quizze" tint="#f0b85f" />
                <StatCard icon={<IconLab />} value={stats.labs} label="Practice Labs" tint="#5fcdd0" />
              </div>

              <div className="aca-level-head">
                <h2>Themengebiete</h2>
                <button className="aca-btn aca-btn-primary" onClick={createTopic}><PlusIcon /> Neues Themengebiet</button>
              </div>
              {topics.length === 0 ? (
                <div className="aca-empty-state">
                  <p>Noch keine Themengebiete angelegt.</p>
                  <p className="aca-empty-state-sub">Importiere die Demo-Inhalte oder lege dein erstes Themengebiet an.</p>
                </div>
              ) : (
                <div className="aca-grid">
                  {topics.map((t) => (
                    <button key={t.id} className="aca-tcard" style={{ '--aca-accent': t.accent || '#6da7ed' }} onClick={() => selectTopic(t.id)}>
                      <div className="aca-tcard-cover">
                        {t.image ? <img src={t.image} alt="" /> : <span className="aca-tcard-cover-fallback"><IconTrack /></span>}
                      </div>
                      <div className="aca-tcard-body">
                        {t.tagline && <span className="aca-tcard-tag">{t.tagline}</span>}
                        <span className="aca-tcard-title">{t.title}</span>
                        <span className="aca-tcard-meta">
                          {(t.courses || []).length} Kurse · {(t.courses || []).reduce((n, c) => n + (c.lessons || []).length, 0)} Lektionen
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Topic editor + course grid */}
          {topic && !course && (
            <div className="aca-editor">
              <EditorHeader
                kind="Themengebiet"
                icon={<IconTrack />}
                title={topic.title}
                subtitle={topic.tagline}
                accent={topic.accent}
                cover={topic.image}
                id={topic.id}
                status={editStatus}
                onDelete={() => deleteTopic(topic)}
                deleteLabel="Themengebiet löschen"
                stats={[
                  { value: topic.courses.length, label: topic.courses.length === 1 ? 'Kurs' : 'Kurse' },
                  { value: topic.courses.reduce((n, c) => n + (c.lessons || []).length, 0), label: 'Lektionen' },
                ]}
              />
              <TrackForm key={topic.id} topic={topic} registerStatus={setEditStatus} />
              <div className="aca-level-head">
                <div>
                  <h2>Kurse</h2>
                  <p className="aca-level-sub">Die Unterkategorien dieses Themengebiets – jeder Kurs bündelt mehrere Lektionen.</p>
                </div>
                <button className="aca-btn aca-btn-primary" onClick={() => createCourse(topic)}><PlusIcon /> Neuer Kurs</button>
              </div>
              {topic.courses.length === 0 ? (
                <div className="aca-empty-state"><p>Noch keine Kurse in diesem Themengebiet.</p></div>
              ) : (
                <div className="aca-grid aca-grid-sm">
                  {topic.courses.map((c) => (
                    <button key={c.id} className="aca-ccard" style={{ '--aca-accent': topic.accent || '#6da7ed' }} onClick={() => selectCourse(topic.id, c.id)}>
                      <span className="aca-ccard-icon"><IconCourse /></span>
                      <span className="aca-ccard-title">{c.title}</span>
                      <span className="aca-ccard-meta">{(c.lessons || []).length} Lektionen{c.badge?.name ? ` · ${c.badge.name}` : ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Course editor + lesson grid */}
          {topic && course && !lesson && (
            <div className="aca-editor">
              <EditorHeader
                kind="Kurs"
                icon={<IconCourse />}
                title={course.title}
                subtitle={course.subtitle}
                accent={topic.accent}
                id={course.id}
                parent={topic.title}
                status={editStatus}
                onDelete={() => deleteCourse(topic, course)}
                deleteLabel="Kurs löschen"
                stats={[{ value: course.lessons.length, label: course.lessons.length === 1 ? 'Lektion' : 'Lektionen' }]}
              />
              <CourseForm key={course.id} topicId={topic.id} course={course} accent={topic.accent} registerStatus={setEditStatus} />
              <div className="aca-level-head">
                <div>
                  <h2>Lernpfad</h2>
                  <p className="aca-level-sub">Genau die Reihenfolge, die der User später durchläuft: Schritt für Schritt von oben nach unten – am Ende das Abzeichen.</p>
                </div>
                <button className="aca-btn aca-btn-primary" onClick={() => createLesson(topic, course)}><PlusIcon /> Neue Lektion</button>
              </div>
              {course.lessons.length === 0 ? (
                <div className="aca-empty-state">
                  <p>Noch keine Lektionen in diesem Kurs.</p>
                  <p className="aca-empty-state-sub">Lege die erste Lektion an – sie wird zum ersten Schritt im Lernpfad.</p>
                </div>
              ) : (
                <CoursePath
                  topic={topic}
                  course={course}
                  onSelectLesson={(lid) => selectLesson(topic.id, course.id, lid)}
                  onMove={(index, dir) => moveLesson(topic, course, index, dir)}
                />
              )}
            </div>
          )}

          {/* Lesson editor */}
          {topic && course && lesson && (
            <div className="aca-editor">
              <EditorHeader
                kind={`${(STEP_META[resolveLessonType(lesson)] || {}).label || 'Lektion'}`}
                icon={(STEP_META[resolveLessonType(lesson)] || {}).icon || <IconLesson />}
                title={lesson.title}
                subtitle={lesson.label}
                accent={(STEP_META[resolveLessonType(lesson)] || {}).color || topic.accent}
                id={lesson.id}
                parent={`${topic.title} · ${course.title}`}
                status={editStatus}
                onDelete={() => deleteLesson(topic, course, lesson)}
                deleteLabel="Lektion löschen"
              />
              <LessonForm key={lesson.id} topicId={topic.id} courseId={course.id} lesson={lesson} accent={topic.accent} registerStatus={setEditStatus} />
            </div>
          )}
        </div>
      </main>

      {modal && <NameModal data={modal} onClose={() => setModal(null)} />}
      {confirm && <ConfirmModal data={confirm} onClose={() => setConfirm(null)} />}
      {lessonModal && <LessonCreateModal onCreate={lessonModal.onCreate} onClose={() => setLessonModal(null)} />}
    </div>
  );
};

export default AcademyAdmin;
